import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import fr from './locales/fr.json';
import en from './locales/en.json';
import es from './locales/es.json';
import hi from './locales/hi.json';
import pt from './locales/pt.json';

// Supported languages
export const SUPPORTED_LANGUAGES = ['fr', 'en', 'es', 'hi', 'pt'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Language metadata for UI (flags dropdown)
export const LANGUAGE_CONFIG: Record<SupportedLanguage, { name: string; nativeName: string; flag: string }> = {
  fr: { name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  en: { name: 'English', nativeName: 'English', flag: '🇺🇸' },
  es: { name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  hi: { name: 'Hindi', nativeName: 'हिंदी', flag: '🇮🇳' },
  pt: { name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' }
};

/**
 * Detect browser language and map to supported language
 * Checks navigator.languages (array of preferred languages) and navigator.language
 */
function detectBrowserLanguage(): SupportedLanguage {
  // Get browser languages (ordered by preference)
  const browserLanguages = navigator.languages || [navigator.language];

  for (const lang of browserLanguages) {
    // Get the primary language code (e.g., 'fr-FR' -> 'fr', 'en-US' -> 'en')
    const primaryLang = lang.split('-')[0].toLowerCase();

    if (SUPPORTED_LANGUAGES.includes(primaryLang as SupportedLanguage)) {
      return primaryLang as SupportedLanguage;
    }
  }

  // Default to English if no supported language found
  return 'en';
}

/**
 * Get the initial language:
 * 1. User's manually saved preference (localStorage with 'userChosen' flag)
 * 2. Browser language detection
 * 3. Fallback to English
 *
 * Note: We only use localStorage if the user explicitly changed the language.
 * This ensures browser language detection works correctly on fresh visits.
 */
function getInitialLanguage(): SupportedLanguage {
  const savedLanguage = localStorage.getItem('language');
  const userChoseLanguage = localStorage.getItem('languageUserChosen') === 'true';

  // Only use saved language if user explicitly chose it (via dropdown)
  if (userChoseLanguage && savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage as SupportedLanguage)) {
    return savedLanguage as SupportedLanguage;
  }

  // Clear old localStorage entries that were set before this fix
  if (savedLanguage && !userChoseLanguage) {
    localStorage.removeItem('language');
  }

  return detectBrowserLanguage();
}

const initialLanguage = getInitialLanguage();

// Set document lang attribute on initial load
if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLanguage;
}

i18n
  .use(initReactI18next)
  .init({
    resources: {
      fr: { translation: fr },
      en: { translation: en },
      es: { translation: es },
      hi: { translation: hi },
      pt: { translation: pt }
    },
    lng: initialLanguage,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    },
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed'
    }
  });

// Track if this is the initial language set (not user-triggered)
let isInitialLanguageSet = true;

// Save language choice to localStorage only when user manually changes it
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;

  // Only save to localStorage if this is a user-triggered change (not initial load)
  if (!isInitialLanguageSet) {
    localStorage.setItem('language', lng);
    localStorage.setItem('languageUserChosen', 'true');
  }
  isInitialLanguageSet = false;
});

export default i18n;
